import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { ApiErrorResponse } from '@/types/api';
import { AuthConfirmResetPayload } from '@/types/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useUserPasswordReset } from '../hooks/useAuth';

export type ResetPasswordDialogForValues = AuthConfirmResetPayload;

const FORM_SCHEMA = yup.object().shape({
  token: yup.string().required('Token is required'),
  newPassword: yup.string().required('New password is required'),
  newPasswordConfirmation: yup
    .string()
    .required('Password confirmation is required')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

type ResetPasswordDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle'
> & {
  token?: string;
  onSuccess: (email?: string) => void;
};

export function ResetPasswordDialog({
  onSuccess,
  token,
  ...props
}: ResetPasswordDialog) {
  const [_, email] = atob(token ?? '')?.split(':') ?? [];

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordDialogForValues>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      token: token ?? '',
      newPassword: '',
      newPasswordConfirmation: '',
    },
  });

  const { mutateAsync: resetUserPassword, isPending } = useUserPasswordReset();

  const handlePasswordResetRequest = async ({
    token,
    newPassword,
    newPasswordConfirmation,
  }: ResetPasswordDialogForValues) => {
    try {
      await resetUserPassword({
        token,
        newPassword,
        newPasswordConfirmation,
      });
      onSuccess(email);
      enqueueSnackbar('Password reset successfully. You can now log in.', {
        variant: 'success',
      });
    } catch (error) {
      if ((error as ApiErrorResponse).statusCode === 400) {
        return setError('root.serverError', {
          type: 'manual',
          message:
            (error as ApiErrorResponse).message ??
            'An error occurred. Please try again later.',
        });
      }
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      {...props}
      title={`Set your new password`}
      subTitle="Enter a new password and confirm"
      onClose={(...args) => {
        reset();
        props.onClose?.(...args);
      }}
    >
      <form
        onSubmit={handleSubmit(handlePasswordResetRequest)}
        className="flex flex-col justify-end gap-3 pt-2"
      >
        {errors.root?.serverError?.message && (
          <ul className="text-red-500 list-disc">
            {errors.root.serverError.message?.split(';').map((error, index) => (
              <li key={index}>
                {error}
                <br />
              </li>
            ))}
          </ul>
        )}
        <Controller
          control={control}
          name="newPassword"
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type="password"
              placeholder="New password"
              aria-label="New password"
              hiddenLabel
            />
          )}
        />
        {errors.newPassword?.message && (
          <p className="text-sm text-red-500">{errors.newPassword.message}</p>
        )}
        <input type="hidden" value={token} />
        <Controller
          control={control}
          name="newPasswordConfirmation"
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type="password"
              placeholder="Confirm password"
              aria-label="Confirm password"
            />
          )}
        />
        {errors.newPasswordConfirmation?.message && (
          <p className="text-sm text-red-500">
            {errors.newPasswordConfirmation.message}
          </p>
        )}

        <Button
          type="submit"
          variant="contained"
          className="w-full mt-6"
          loading={isPending}
          size="small"
        >
          Change password
        </Button>
      </form>
    </Modal>
  );
}
